/**
 * Data layer for the Pages editor's Lists mode — the record-list twin of
 * usePageDocs. Same contract, per RECORD instead of per page:
 *
 *   - Lists load lazily (first open) from their admin API route.
 *   - Every edit stages a local draft and AUTOSAVES it 800ms after the
 *     last change as a PATCH of just the changed fields (null clears a
 *     nullable column — the form clears optionals to absent).
 *   - VALIDATION GATE: a draft that fails RECORD_SCHEMAS[list] never hits
 *     the wire; the panel lists what to fix and the draft stays local.
 *   - Reorders are one PUT /reorder per drag (a whole flat list, or one
 *     group of a grouped list), applied optimistically.
 *   - Refs mirror state for the save path — reading state through a
 *     setState updater is the Forge never-saves bug.
 *
 * Conflict model: last-writer-wins per record (audit_log keeps both).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RECORD_SCHEMAS, type ListKey } from '@vv/sections';
import { API_BASE } from './config';
import { authHeaders } from './auth';
import {
  LIST_DEFS,
  editableJson,
  editableKeys,
  groupOf,
  recKey,
  type ListRecord,
} from './lists';

const AUTOSAVE_DEBOUNCE_MS = 800;

export interface RecordIssue {
  path: string;
  message: string;
}

type Records = Partial<Record<ListKey, ListRecord[]>>;

/** Status-map key for a group's pending order ("drips#order:" / "faqs#order:glp1"). */
const orderKey = (list: ListKey, group: string) => `${list}#order:${group}`;

async function errorOf(res: Response): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return body.error || `HTTP ${res.status}`;
}

export function useLists() {
  const [records, setRecords] = useState<Records>({});
  const [loadingLists, setLoadingLists] = useState<Set<ListKey>>(new Set());
  const [loadErrors, setLoadErrors] = useState<Partial<Record<ListKey, string>>>({});
  /** editableJson of the last state the server confirmed, per record key. */
  const [saved, setSaved] = useState<Record<string, string>>({});
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());
  const [failedKeys, setFailedKeys] = useState<Set<string>>(new Set());
  const [issues, setIssues] = useState<Record<string, RecordIssue[]>>({});
  const [failReason, setFailReason] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);

  const recordsRef = useRef(records);
  recordsRef.current = records;
  const savedRef = useRef(saved);
  savedRef.current = saved;
  /** Records as first loaded/created — the preview only re-sends a photo
   * that actually changed (re-pointing every <img> would drop the
   * resized srcset for full-size originals). */
  const loadedRef = useRef<Record<string, ListRecord>>({});
  const requested = useRef<Set<ListKey>>(new Set());
  const timers = useRef<Map<string, number>>(new Map());
  const timerTargets = useRef<Map<string, { list: ListKey; id: string }>>(new Map());
  const inFlight = useRef<Set<string>>(new Set());
  const queued = useRef<Set<string>>(new Set());
  /** Latest wanted order per group whose PUT failed — retryFailed resends. */
  const failedOrders = useRef<Map<string, { list: ListKey; group: string; ids: string[] }>>(
    new Map(),
  );
  /** Records deleted this session — a PATCH that was already on the wire
   * when its record was deleted must not resurrect a "not saved" flag
   * (there'd be nothing left to retry, so the chip would stick). */
  const deletedKeys = useRef<Set<string>>(new Set());
  const flushRef = useRef<(list: ListKey, id: string) => Promise<void>>(async () => {});

  const markSet = (setter: typeof setSavingKeys, key: string, on: boolean) =>
    setter((prev) => {
      if (prev.has(key) === on) return prev;
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  const noteAuthFailure = (res: Response, message: string) =>
    setFailReason(
      res.status === 401 || res.status === 403
        ? 'Session expired — sign in again in a new tab, your edits are safe here until you close this one'
        : message,
    );

  // ── Load ───────────────────────────────────────────────────────────
  const ensureLoaded = useCallback(async (list: ListKey, force = false) => {
    if (!force && requested.current.has(list)) return;
    requested.current.add(list);
    const def = LIST_DEFS[list];
    setLoadingLists((prev) => new Set(prev).add(list));
    setLoadErrors((prev) => {
      if (!prev[list]) return prev;
      const next = { ...prev };
      delete next[list];
      return next;
    });
    try {
      const res = await fetch(`${API_BASE}${def.apiPath}`, { headers: authHeaders(false) });
      if (!res.ok) throw new Error(await errorOf(res));
      const data = (await res.json()) as Record<string, ListRecord[]>;
      const rows = data[def.listProp] ?? [];
      setRecords((prev) => ({ ...prev, [list]: rows }));
      setSaved((prev) => {
        const next = { ...prev };
        for (const r of rows) next[recKey(list, r.id)] = editableJson(list, r);
        return next;
      });
      for (const r of rows) loadedRef.current[recKey(list, r.id)] = r;
    } catch (e) {
      requested.current.delete(list);
      setLoadErrors((prev) => ({ ...prev, [list]: (e as Error).message }));
    } finally {
      setLoadingLists((prev) => {
        const next = new Set(prev);
        next.delete(list);
        return next;
      });
    }
  }, []);

  // ── Autosave engine ────────────────────────────────────────────────
  const flush = useCallback(async (list: ListKey, id: string) => {
    const key = recKey(list, id);
    if (inFlight.current.has(key)) {
      queued.current.add(key);
      return;
    }
    inFlight.current.add(key);
    try {
      const r = recordsRef.current[list]?.find((x) => x.id === id);
      if (!r) return;
      const json = editableJson(list, r);
      const clearIssues = () =>
        setIssues((prev) => {
          if (!prev[key]) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
      // Clean again (e.g. a flagged field typed back to its saved value) —
      // nothing to send, but the "fix" / "not saved" flags must still
      // clear, or retry would hit this same early return forever.
      if (json === savedRef.current[key]) {
        clearIssues();
        markSet(setFailedKeys, key, false);
        return;
      }

      const parsed = RECORD_SCHEMAS[list].safeParse(r);
      if (!parsed.success) {
        setIssues((prev) => ({
          ...prev,
          [key]: parsed.error.issues.map((i) => ({
            path: i.path.join('.'),
            message:
              i.code === 'too_small' && i.type === 'string' && i.minimum === 1
                ? 'Required'
                : i.message,
          })),
        }));
        return;
      }
      clearIssues();

      const cur = JSON.parse(json) as Record<string, unknown>;
      const before = JSON.parse(savedRef.current[key] ?? '{}') as Record<string, unknown>;
      const patch: Record<string, unknown> = {};
      for (const k of Object.keys(cur)) {
        if (JSON.stringify(cur[k]) !== JSON.stringify(before[k])) patch[k] = cur[k];
      }

      markSet(setSavingKeys, key, true);
      markSet(setFailedKeys, key, false);
      try {
        const res = await fetch(`${API_BASE}${LIST_DEFS[list].apiPath}/${encodeURIComponent(id)}`, {
          method: 'PATCH',
          headers: authHeaders(),
          body: JSON.stringify(patch),
        });
        if (deletedKeys.current.has(key)) return; // deleted mid-flight — moot
        if (!res.ok) {
          const msg = await errorOf(res);
          noteAuthFailure(res, msg);
          throw new Error(msg);
        }
        // Snapshot what we SENT — anything typed mid-flight differs and the
        // next debounce tick flushes it.
        setSaved((prev) => ({ ...prev, [key]: json }));
        setLastSavedAt(Date.now());
        setFailReason(null);
      } catch {
        if (!deletedKeys.current.has(key)) markSet(setFailedKeys, key, true);
      } finally {
        markSet(setSavingKeys, key, false);
      }
    } finally {
      inFlight.current.delete(key);
      if (queued.current.delete(key)) void flushRef.current(list, id);
    }
  }, []);
  flushRef.current = flush;

  const scheduleFlush = useCallback((list: ListKey, id: string) => {
    const key = recKey(list, id);
    const existing = timers.current.get(key);
    if (existing) window.clearTimeout(existing);
    timerTargets.current.set(key, { list, id });
    timers.current.set(
      key,
      window.setTimeout(() => {
        timers.current.delete(key);
        timerTargets.current.delete(key);
        void flushRef.current(list, id);
      }, AUTOSAVE_DEBOUNCE_MS),
    );
  }, []);

  // Unmount FIRES pending saves instead of dropping them.
  useEffect(() => {
    const t = timers.current;
    const targets = timerTargets.current;
    return () => {
      for (const [key, handle] of t) {
        window.clearTimeout(handle);
        const target = targets.get(key);
        if (target) void flushRef.current(target.list, target.id);
      }
      t.clear();
    };
  }, []);

  // ── Edits ──────────────────────────────────────────────────────────
  /** The single write entrypoint — replace a record draft + autosave. */
  const setRecord = useCallback(
    (list: ListKey, id: string, next: Record<string, unknown>) => {
      setRecords((prev) => {
        const rows = prev[list];
        if (!rows) return prev;
        return {
          ...prev,
          [list]: rows.map((r) => (r.id === id ? ({ ...next, id } as ListRecord) : r)),
        };
      });
      scheduleFlush(list, id);
    },
    [scheduleFlush],
  );

  const toggleActive = useCallback(
    (list: ListKey, id: string) => {
      const r = recordsRef.current[list]?.find((x) => x.id === id);
      if (r) setRecord(list, id, { ...r, is_active: !r.is_active });
    },
    [setRecord],
  );

  /** Put the group's records in `ids` order (optimistic) and PUT it. */
  const sendOrder = useCallback(async (list: ListKey, group: string, ids: string[]) => {
    const key = orderKey(list, group);
    markSet(setSavingKeys, key, true);
    try {
      const res = await fetch(`${API_BASE}${LIST_DEFS[list].apiPath}/reorder`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) {
        const msg = await errorOf(res);
        noteAuthFailure(res, msg);
        throw new Error(msg);
      }
      failedOrders.current.delete(key);
      markSet(setFailedKeys, key, false);
      setLastSavedAt(Date.now());
    } catch {
      failedOrders.current.set(key, { list, group, ids });
      markSet(setFailedKeys, key, true);
    } finally {
      markSet(setSavingKeys, key, false);
    }
  }, []);

  const reorder = useCallback(
    (list: ListKey, group: string, ids: string[]) => {
      setRecords((prev) => {
        const rows = prev[list];
        if (!rows) return prev;
        const rank = new Map(ids.map((id, i) => [id, i]));
        const inGroup = rows.filter((r) => rank.has(r.id));
        inGroup.sort((a, b) => rank.get(a.id)! - rank.get(b.id)!);
        // Refill the group's slots in the new order; other groups untouched.
        let i = 0;
        const next = rows.map((r) => {
          if (!rank.has(r.id)) return r;
          const moved = inGroup[i]!;
          i += 1;
          return { ...moved, display_order: (rank.get(moved.id)! + 1) * 10 };
        });
        return { ...prev, [list]: next };
      });
      void sendOrder(list, group, ids);
    },
    [sendOrder],
  );

  const create = useCallback(
    async (
      list: ListKey,
      group?: string,
    ): Promise<{ ok: true; id: string } | { ok: false; error: string }> => {
      const def = LIST_DEFS[list];
      const siblings = (recordsRef.current[list] ?? []).filter(
        (r) => groupOf(list, r) === (group ?? ''),
      );
      try {
        const res = await fetch(`${API_BASE}${def.apiPath}`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify(def.createBody(group, siblings)),
        });
        if (!res.ok) return { ok: false, error: await errorOf(res) };
        const row = ((await res.json()) as Record<string, ListRecord>)[def.itemProp]!;
        insertRow(list, row, null);
        return { ok: true, id: row.id };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },
    [],
  );

  /** Add a server-confirmed row to local state — after `afterId`, else at
   * the end of its group. */
  function insertRow(list: ListKey, row: ListRecord, afterId: string | null) {
    const key = recKey(list, row.id);
    loadedRef.current[key] = row;
    setSaved((prev) => ({ ...prev, [key]: editableJson(list, row) }));
    setRecords((prev) => {
      const rows = [...(prev[list] ?? [])];
      let at = afterId ? rows.findIndex((r) => r.id === afterId) + 1 : 0;
      if (!afterId || at === 0) {
        const g = groupOf(list, row);
        const lastInGroup = rows.map((r) => groupOf(list, r)).lastIndexOf(g);
        at = lastInGroup === -1 ? rows.length : lastInGroup + 1;
      }
      rows.splice(at, 0, row);
      return { ...prev, [list]: rows };
    });
    setLastSavedAt(Date.now());
  }

  /** Copy a record: POST what the create route accepts, then hand the
   * remaining fields to the normal autosave (a draft over the server row)
   * — so a failed follow-up save shows the usual "not saved — retry"
   * instead of a copy silently missing its photo. The copy starts hidden
   * and is ordered right after its source. */
  const duplicate = useCallback(
    async (
      list: ListKey,
      id: string,
    ): Promise<{ ok: true; id: string } | { ok: false; error: string }> => {
      const def = LIST_DEFS[list];
      const src = recordsRef.current[list]?.find((r) => r.id === id);
      if (!src) return { ok: false, error: 'Item not loaded' };
      const body: Record<string, unknown> = {};
      for (const k of def.createKeys) {
        if (src[k] !== undefined && src[k] !== null) body[k] = src[k];
      }
      body[def.titleField] = `${String(src[def.titleField] ?? '')} (copy)`;
      if ('page' in body) body.is_active = false; // faqs/steps create routes default to shown
      try {
        const res = await fetch(`${API_BASE}${def.apiPath}`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify(body),
        });
        if (!res.ok) return { ok: false, error: await errorOf(res) };
        const row = ((await res.json()) as Record<string, ListRecord>)[def.itemProp]!;
        insertRow(list, row, id);
        const rest: Record<string, unknown> = {};
        for (const k of editableKeys(list)) {
          if (k === 'is_active' || k === def.titleField || def.createKeys.includes(k)) continue;
          const v = src[k] ?? null;
          if (JSON.stringify(v) !== JSON.stringify(row[k] ?? null)) rest[k] = v;
        }
        if (Object.keys(rest).length > 0) setRecord(list, row.id, { ...row, ...rest });
        const group = groupOf(list, row);
        const ids = (recordsRef.current[list] ?? [])
          .filter((r) => groupOf(list, r) === group && r.id !== row.id)
          .map((r) => r.id);
        ids.splice(ids.indexOf(id) + 1, 0, row.id);
        void sendOrder(list, group, ids);
        return { ok: true, id: row.id };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },
    [sendOrder, setRecord],
  );

  const remove = useCallback(async (list: ListKey, id: string): Promise<boolean> => {
    const key = recKey(list, id);
    const pending = timers.current.get(key);
    if (pending) {
      window.clearTimeout(pending);
      timers.current.delete(key);
      timerTargets.current.delete(key);
    }
    // Marked BEFORE the request: a PATCH already on the wire may settle
    // after the row is gone (see deletedKeys).
    deletedKeys.current.add(key);
    try {
      const res = await fetch(`${API_BASE}${LIST_DEFS[list].apiPath}/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: authHeaders(false),
      });
      if (!res.ok) {
        deletedKeys.current.delete(key);
        const msg = await errorOf(res);
        noteAuthFailure(res, msg);
        return false;
      }
    } catch (e) {
      deletedKeys.current.delete(key);
      setFailReason((e as Error).message);
      return false;
    }
    setRecords((prev) => ({ ...prev, [list]: (prev[list] ?? []).filter((r) => r.id !== id) }));
    for (const setter of [setSavingKeys, setFailedKeys]) markSet(setter, key, false);
    setIssues((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setLastSavedAt(Date.now());
    return true;
  }, []);

  const retryFailed = useCallback(() => {
    for (const key of failedKeys) {
      const order = failedOrders.current.get(key);
      if (order) {
        void sendOrder(order.list, order.group, order.ids);
        continue;
      }
      const [list, id] = key.split(/:(.*)/s) as [ListKey, string];
      if (!list || !id) continue;
      // Gone since it failed (deleted) — nothing to retry, drop the flag.
      if (!recordsRef.current[list]?.some((r) => r.id === id)) {
        markSet(setFailedKeys, key, false);
        continue;
      }
      void flushRef.current(list, id);
    }
  }, [failedKeys, sendOrder]);

  // ── Derived status ─────────────────────────────────────────────────
  const dirtyKeys = useMemo(() => {
    const out = new Set<string>();
    for (const [list, rows] of Object.entries(records) as Array<[ListKey, ListRecord[]]>) {
      for (const r of rows) {
        const key = recKey(list, r.id);
        if (saved[key] !== undefined && editableJson(list, r) !== saved[key]) out.add(key);
      }
    }
    return out;
  }, [records, saved]);

  // Saving = a request on the wire, or a valid dirty draft waiting for
  // its debounce (blocked drafts show as "fix issues" instead).
  const saving =
    savingKeys.size > 0 || [...dirtyKeys].some((k) => !issues[k]?.length && !failedKeys.has(k));

  useEffect(() => {
    if (!saving && dirtyKeys.size === 0) return;
    function warn(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [saving, dirtyKeys.size]);

  const loadedRecord = useCallback(
    (list: ListKey, id: string): ListRecord | undefined => loadedRef.current[recKey(list, id)],
    [],
  );

  return {
    records,
    ensureLoaded,
    loadingLists,
    loadErrors,
    setRecord,
    toggleActive,
    reorder,
    create,
    duplicate,
    remove,
    retryFailed,
    loadedRecord,
    issues,
    dirtyKeys,
    saving,
    failedCount: failedKeys.size,
    failReason,
    lastSavedAt,
  };
}

export type ListsApi = ReturnType<typeof useLists>;
